/**
 * Record summary card — the one card shape for a feed of incoming work.
 *
 * Leads, Calls, and Form Entries are the three record types a LeadLoop portal
 * puts in front of a client, and they had grown three unrelated cards: the
 * LeadLoop queue drew its own by hand, Form Entries and Calls used a shared
 * pair built around an avatar and a stack of pills, and the Leads list used the
 * generic label/value grid. The same fact — who this is, how to reach them,
 * when it arrived, what state it is in — landed in a different place on each,
 * so reading a mixed feed meant re-learning the layout per card.
 *
 * This is that card, once. Reading order is fixed, because a grid of cards is
 * scanned column-first and the eye should not have to search:
 *
 *   1. type strip   what kind of record this is, before anything else
 *   2. title + badges   what it is, and what state it is in
 *   3. contact line     the phone number and email, tappable
 *   4. meta             quiet context: name, company, source, when
 *   5. summary          two lines of what they actually said
 *   6. highlight        the one thing this record wants, when it wants one
 *   7. footer           actions, with the primary one pinned right
 *
 * Everything below the title is optional, so a Call card and a Lead card differ
 * by what they have rather than by how they are built.
 */

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Mail, Phone } from 'lucide-react';
import { Card } from '../ui/card';
import {
  RecordTypeStrip,
  RegisteredRecordTypeStrip,
  type RecordTypeId,
  type RegisteredRecordTypeDefinition,
} from './record-type-strip';
import { cn } from '../lib/utils';

/**
 * Phone and email, made scannable.
 *
 * These two facts decide what the reader does next, and they used to sit
 * unstyled in the middle of a run-on metadata line — company, phone, email,
 * time — where the eye had to parse the whole sentence to find a number to
 * dial. They render as their own row, each with its own icon, each actionable,
 * and each stopping propagation so tapping a number dials it instead of opening
 * the record behind it.
 */
export function ContactLine({
  phone,
  email,
  stacked,
  className,
}: {
  phone?: string | null;
  email?: string | null;
  /** One per line — for a narrow table cell, where two side by side truncate. */
  stacked?: boolean;
  className?: string;
}) {
  if (!phone && !email) return null;
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]',
        stacked && 'flex-col items-start gap-y-0.5',
        className,
      )}
    >
      {phone && (
        <a
          href={`tel:${phone.replace(/[^\d+]/g, '')}`}
          onClick={(event) => event.stopPropagation()}
          className="inline-flex items-center gap-1.5 font-medium tabular-nums text-foreground hover:text-primary hover:underline"
        >
          <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          {phone}
        </a>
      )}
      {email && (
        <a
          href={`mailto:${email}`}
          onClick={(event) => event.stopPropagation()}
          className="inline-flex min-w-0 items-center gap-1.5 font-medium text-foreground hover:text-primary hover:underline"
        >
          <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="truncate">{email}</span>
        </a>
      )}
    </div>
  );
}

/**
 * One quiet fact in the meta row, with an optional icon.
 *
 * Meta items are context, never decisions — the company someone works for, the
 * form an entry came through, how long a call lasted. They are muted and they
 * wrap, so a card with six of them degrades into two lines rather than
 * truncating the one the reader wanted.
 */
export function RecordMeta({
  icon: Icon,
  children,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  children: ReactNode;
}) {
  if (children === undefined || children === null || children === '') return null;
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden />}
      <span className="truncate">{children}</span>
    </span>
  );
}

export interface RecordSummaryCardProps {
  recordType?: RecordTypeId;
  recordTypeDefinition?: RegisteredRecordTypeDefinition;
  recordTypeIcon?: LucideIcon;
  title: string;
  /** Opens the record. Also fired by clicking the card body. */
  onOpen: () => void;
  /**
   * State badges, top right. Keep this to what a reader cannot already see:
   * one lifecycle badge and, at most, one attribute.
   */
  badges?: ReactNode;
  /** One line under the title — a person's name, a form name. */
  subtitle?: ReactNode;
  phone?: string | null;
  email?: string | null;
  /** `RecordMeta` children. Muted, wrapping, never load-bearing. */
  meta?: ReactNode;
  /** What they said, clamped to two lines. */
  summary?: ReactNode;
  /** The one thing this record wants next, in the accent colour. */
  highlight?: ReactNode;
  /** Controls along the bottom edge, left of the primary action. */
  actions?: ReactNode;
  /** The primary action, pinned bottom right. */
  primaryAction?: ReactNode;
  /** Quiet state/context displayed in the record-type strip. */
  stripContext?: ReactNode;
  /** Selectable record control, anchored at the strip's top-right edge. */
  selection?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string;
    disabled?: boolean;
  };
  /** Last-updated or equivalent record metadata pinned to the card footer. */
  footerMeta?: ReactNode;
  /** Pull the eye across a grid: something here needs a person. */
  flagged?: boolean;
  /** This record is open in the pane beside the list. */
  selected?: boolean;
  /**
   * Draw as a proposal rather than a record: dashed border, for intake that has
   * no business record behind it yet.
   */
  provisional?: boolean;
  className?: string;
}

export function RecordSummaryCard({
  recordType,
  recordTypeDefinition,
  recordTypeIcon,
  title,
  onOpen,
  badges,
  subtitle,
  phone,
  email,
  meta,
  summary,
  highlight,
  actions,
  primaryAction,
  stripContext,
  selection,
  footerMeta,
  flagged = false,
  selected = false,
  provisional = false,
  className,
}: RecordSummaryCardProps) {
  const hasFooter = Boolean(actions || primaryAction || footerMeta);
  return (
    <Card
      onClick={onOpen}
      className={cn(
        // Padding lives on the inner column so the identity strip can run edge
        // to edge along the top.
        'flex h-full min-w-0 cursor-pointer flex-col overflow-hidden p-0 transition-all',
        'hover:-translate-y-px hover:border-primary/40 hover:shadow-md',
        provisional && 'border-dashed',
        flagged && 'border-primary bg-selection',
        selected && 'border-primary bg-selection ring-1 ring-primary',
        className,
      )}
    >
      {recordTypeDefinition && recordTypeIcon ? (
        <RegisteredRecordTypeStrip
          definition={recordTypeDefinition}
          icon={recordTypeIcon}
          trailing={
            stripContext || selection ? (
              <span className="flex min-w-0 items-center gap-2">
                {stripContext && <span className="truncate">{stripContext}</span>}
                {selection && <RecordSelection selection={selection} />}
              </span>
            ) : undefined
          }
        />
      ) : recordType ? (
        <RecordTypeStrip
          type={recordType}
          trailing={
          stripContext || selection ? (
            <span className="flex min-w-0 items-center gap-2">
              {stripContext && <span className="truncate">{stripContext}</span>}
              {selection && <RecordSelection selection={selection} />}
            </span>
          ) : undefined
        }
        />
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-3 p-4">
        <div className="min-w-0">
          {/*
            Title and badges share a row of their own, so a long email in the
            contact line below cannot push the badges onto a second line and
            leave two cards in the same grid with their state at different
            heights.
          */}
          <div className="flex items-start justify-between gap-3">
            {/*
              The keyboard path. The card as a whole is NOT role="button": it
              contains buttons and links, and a button may not nest interactive
              content. A card-level Space/Enter handler also swallowed the space
              bar inside any dialog the card opens.
            */}
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onOpen();
              }}
              className="min-w-0 rounded-sm text-left text-[15px] font-semibold leading-tight text-foreground underline-offset-2 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="line-clamp-2">{title}</span>
            </button>
            {badges && (
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                {badges}
              </div>
            )}
          </div>
          {subtitle && (
            <div className="mt-1 truncate text-[12px] leading-tight text-muted-foreground">
              {subtitle}
            </div>
          )}
          <ContactLine phone={phone} email={email} className="mt-1.5" />
          {meta && (
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
              {meta}
            </div>
          )}
        </div>

        {summary && (
          <p className="line-clamp-2 max-w-[95ch] text-[13px] leading-relaxed text-muted-foreground">
            {summary}
          </p>
        )}

        {highlight && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">{highlight}</div>
        )}

        {hasFooter && (
          /*
            Every control below stops propagation. Without it, opening an email
            composer or a review dialog would also fire the card's own onOpen
            and leave the record open behind whatever was clicked.
          */
          <div
            className="mt-auto flex flex-wrap items-center gap-2 border-t border-border/70 pt-3"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            role="presentation"
          >
            {footerMeta && <span className="text-[11px] text-muted-foreground">{footerMeta}</span>}
            {actions && <div className={cn(footerMeta && 'ml-auto')}>{actions}</div>}
            {primaryAction && <div className="ml-auto">{primaryAction}</div>}
          </div>
        )}
      </div>
    </Card>
  );
}

function RecordSelection({
  selection,
}: {
  selection: NonNullable<RecordSummaryCardProps['selection']>;
}) {
  return (
    <label
      className="flex shrink-0 items-center"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={selection.checked}
        disabled={selection.disabled}
        onChange={(event) => selection.onChange(event.currentTarget.checked)}
        className="h-3.5 w-3.5 accent-primary"
      />
      <span className="sr-only">{selection.label}</span>
    </label>
  );
}
